import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  TextField,
  Typography,
  MenuItem,
  Snackbar,
  Alert,
  IconButton,
  Grid,
} from "@mui/material";
import { useState } from "react";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { RequiredAsterisk } from "../../../components/professional/RequiredAsterisk";
import { FileText, Upload, X } from "lucide-react";
import Select from "../../../components/common/Select";

const EmploymentHistoryForm = ({
  employment = {},
  onSubmit,
  onCancel,
  errors,
  setErrors,
}) => {
  const certificateUrl = import.meta.env.VITE_API_BASE_URL || "";
  
  const [formData, setFormData] = useState({
    id: employment?.id || null,
    duration: employment?.duration || "",
    company_name: employment?.company_name || "",
    job_title: employment?.job_title || "",
    start_date: employment?.start_date || null,
    end_date: employment?.end_date || null,
    is_current: employment?.is_current || false,
    responsibilities: employment?.responsibilities || "",
    nature_of_employer: employment?.nature_of_employer || "",
    major_task_performed: employment?.major_task_performed || "",
    reference_contact: employment?.reference_contact || "",
    types_of_mission: employment?.types_of_mission || "",
    supporting_documents: employment?.supporting_documents || null,
    reason_leaving: employment?.reason_leaving || "",
  });

  const [fileError, setFileError] = useState("");

  const employerOptions = [
    "Airline",
    "Charter Operator",
    "MRO",
    "Training",
    "Military",
    "School",
    "Ground Handling",
    "ATC",
    "UAV Operator",
    "Regulator",
  ].map(opt => ({ label: opt, value: opt }));

  const taskOptions = [
    "Line maintenance on A320 fleet",
    "ATC Area Control for Nairobi FIR",
    "Cabin Crew Safety and services on B787 long haul",
  ].map(opt => ({ label: opt, value: opt }));

  const missionOptions = [
    "Domestic",
    "International",
    "Long Haul",
    "Short Haul",
    "Cargo",
    "VIP",
    "Humanitarian",
    "Military",
  ].map(opt => ({ label: opt, value: opt }));

  const handleChange = (field, value) => {
    let newValue = value;

    if (value instanceof Date && !isNaN(value)) {
      newValue = format(value, "yyyy-MM-dd");
    }

    setFormData((prev) => ({ ...prev, [field]: newValue }));

    if (field === "is_current" && newValue === true) {
      setFormData((prev) => ({ ...prev, end_date: null, start_date: null }));
      if (errors['end_date'] || errors['start_date']) setErrors((prev) => ({
        ...prev,
        end_date: null,
        start_date: null,
      }))
    }

    if (errors[field]) {
      setErrors((prev) => {
        const updated = { ...prev };
        delete updated[field];
        return updated;
      });
    }
  };

  const handleRemoveFile = (e) => {
    e.stopPropagation();
    setFormData((prev) => ({ ...prev, supporting_documents: null }));
  };

  const handleFileUpload = (file) => {
    setFileError("");

    if (!file) return;

    // Check file type
    const validTypes = [
      "image/jpeg",
      "image/png",
      "application/pdf",
      "application/msword",
    ];
    if (!validTypes.includes(file.type)) {
      setFileError("Please select a JPG, PNG, DOCX or PDF file");
      return false;
    }

    // Check file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setFileError("File size must be less than 10MB");
      return false;
    }

    setFormData((prev) => ({ ...prev, supporting_documents: file }));

    return true;
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    handleFileUpload(file);
    // handleChange("supporting_documents", file);
  };

  const handleCloseError = () => {
    setFileError("");
  };

  const validateForm = () => {
    let valid = true;
    let newErrors = {};
    if (!formData.company_name?.trim()) {
      newErrors.company_name = "Company name is required";
      valid = false;
    }
    if (!formData.job_title?.trim()) {
      newErrors.job_title = "Job title is required";
      valid = false;
    }
    if (!formData.is_current && !formData.start_date?.trim()) {
      newErrors.start_date = "Start date is required unless this is your current job";
      valid = false;
    }
    if (!formData.is_current && !formData.end_date?.trim()) {
      newErrors.end_date =
        "End date is required unless this is your current job";
      valid = false;
    }
    if (!formData.responsibilities?.trim()) {
      newErrors.responsibilities = "Responsibilities are required";
      valid = false;
    }
    // if (!formData.reason_leaving?.trim()) {
    //   newErrors.reason_leaving = "Reason(s) for leaving is required";
    //   valid = false;
    // }
    setErrors(newErrors);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    console.log("errors before submit:", errors);
    console.log("Submitting form with data:", formData);
    if (validateForm()) onSubmit(formData);
  };

  return (
    <>
      <form onSubmit={handleSubmit} className="space-y-4 my-3 sentry-mask">
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={2}>
            {/* Company & Job Title */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="company_name"
                fullWidth
                // placeholder="e.g Commercial Pilot"
                value={formData.company_name}
                onChange={(e) => handleChange("company_name", e.target.value)}
                error={!!errors.company_name}
                helperText={errors.company_name}
                label={
                  <span>
                    Company Name <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <TextField
                id="job_title"
                fullWidth
                // placeholder="e.g Commercial Pilot"
                value={formData.job_title}
                onChange={(e) => handleChange("job_title", e.target.value)}
                error={!!errors.job_title}
                helperText={errors.job_title}
                label={
                  <span>
                    Job Title <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>

            {/* Current Job Checkbox */}
            <Grid size={{ xs: 12 }}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={formData.is_current}
                    onChange={(e) =>
                      handleChange("is_current", e.target.checked)
                    }
                    color="primary"
                  />
                }
                label="This is my current employment"
              />
            </Grid>

            {/* Start & End Dates */}
            {!formData.is_current &&
              (

                <>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label={
                        <span>
                          Start Date <RequiredAsterisk />
                        </span>
                      }
                      value={
                        formData.start_date ? new Date(formData.start_date) : null
                      }
                      onChange={(date) =>
                        handleChange(
                          "start_date",
                          date ? format(date, "yyyy-MM-dd") : null,
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors[`start_date`],
                          helperText: errors[`start_date`],
                        },
                      }}
                    />
                  </Grid>

                  <Grid size={{ xs: 12, sm: 6 }}>
                    <DatePicker
                      label={
                        <span>
                          End Date <RequiredAsterisk />
                        </span>
                      }
                      value={formData.end_date ? new Date(formData.end_date) : null}
                      onChange={(date) =>
                        handleChange(
                          "end_date",
                          date ? format(date, "yyyy-MM-dd") : null,
                        )
                      }
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors[`end_date`],
                          helperText: errors[`end_date`],
                        },
                      }}
                    />
                  </Grid>
                </>
              )}

            {/* Nature of Employer */}
            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.nature_of_employer}
                helperText={errors.nature_of_employer}
                label="Nature of Employer"
                onChange={(value) => handleChange("nature_of_employer", value)}
                options={employerOptions}
                value={formData.nature_of_employer}
              />
            </Grid>

            <Grid size={{ xs: 12, sm: 6 }}>
              <Select
                error={!!errors.major_task_performed}
                helperText={errors.major_task_performed}
                label="Major Task Performed"
                onChange={(value) =>
                  handleChange("major_task_performed", value)
                }
                options={taskOptions}
                value={formData.major_task_performed}
              />
            </Grid>

            {/* Reference Contact */}
            <Grid size={{ xs: 12 }}>
              <TextField
                id="reference_contact"
                fullWidth
                // placeholder=""
                value={formData.reference_contact}
                onChange={(e) =>
                  handleChange("reference_contact", e.target.value)
                }
                error={!!errors.reference_contact}
                helperText={errors.reference_contact}
                label="Reference Contact"
              />
            </Grid>

            {/* Responsibilities */}
            <Grid size={{ xs: 12 }}>
              <TextField
                id="responsibilities"
                fullWidth
                multiline
                minRows={3}
                // placeholder="Responsibilities & Achievements"
                value={formData.responsibilities}
                onChange={(e) =>
                  handleChange("responsibilities", e.target.value)
                }
                error={!!errors.responsibilities}
                helperText={errors.responsibilities}
                label={
                  <span>
                    Responsibilities & Achievements <RequiredAsterisk />
                  </span>
                }
              />
            </Grid>

            {/* Types of Mission */}
            {/* <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InputLabel htmlFor="types_of_mission" sx={{ fontWeight: 600 }}>
                Types of Mission
              </InputLabel>
              <TextField
                select
                label="Types of Mission"
                value={formData.types_of_mission}
                onChange={(e) =>
                  handleChange("types_of_mission", e.target.value)
                }
                fullWidth
              >
                {missionOptions.map((opt) => (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InputLabel htmlFor="reference_contact" sx={{ fontWeight: 600 }}>
                Reference Contact
              </InputLabel>

              <FormControl fullWidth error={!!errors[`reference_contact`]}>
                <input
                  type="text"
                  id="reference_contact"
                  placeholder="Reference Contact"
                  value={formData.reference_contact}
                  onChange={(e) =>
                    handleChange("reference_contact", e.target.value)
                  }
                  className={`p-4 border rounded-md focus:outline-none focus:ring-2 ${
                    errors["reference_contact"]
                      ? "focus:ring-red-500 border-red-500 focus:border-2"
                      : "focus:ring-blue-500 border-gray-400"
                  }`}
                />

                {errors[`reference_contact`] && (
                  <Typography variant="caption" color="error">
                    {errors[`reference_contact`]}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </div> */}

            {/* Reason Leaving */}
            {/* <div className="mb-4">
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <InputLabel htmlFor="reason_leaving" sx={{ fontWeight: 600 }}>
                Reason for leaving <RequiredAsterisk />
              </InputLabel>
              <FormControl fullWidth error={!!errors[`reason_leaving`]}>
                <textarea
                  id="reason_leaving"
                  placeholder="Reason for leaving"
                  value={formData.reason_leaving}
                  onChange={(e) =>
                    handleChange("reason_leaving", e.target.value)
                  }
                  rows={2}
                  className={`w-full p-3 border rounded-md focus:outline-none focus:ring-2 ${
                    errors["reason_leaving"]
                      ? "focus:ring-red-500 border-red-500 focus:border-2"
                      : "focus:ring-blue-500 border-gray-400"
                  }`}
                />
                {errors[`reason_leaving`] && (
                  <Typography variant="caption" color="error">
                    {errors[`reason_leaving`]}
                  </Typography>
                )}
              </FormControl>
            </Box>
          </div> */}

            {/* Supporting Documents */}
            <Grid size={{ xs: 12 }}>
              <InputLabel
                htmlFor="supporting_documents"
                sx={{ fontWeight: 600 }}
              >
                Supporting Documents
              </InputLabel>
              <div
                className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center cursor-pointer hover:border-blue-400 transition-colors duration-200"
                onClick={() =>
                  document.getElementById(`supporting_documents`)?.click()
                }
              >
                <input
                  type="file"
                  id={`supporting_documents`}
                  className="hidden"
                  accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                  onChange={handleFileChange}
                />

                {formData.supporting_documents ? (
                  <div className="flex items-center justify-between bg-blue-50 rounded-md p-3">
                    <div className="flex items-center">
                      <FileText className="text-blue-500 mr-2" size={20} />
                      <Typography
                        component="a"
                        href={
                          typeof formData.supporting_documents === "string"
                            ? `${certificateUrl}${formData.supporting_documents}`
                            : URL.createObjectURL(formData.supporting_documents) // preview for new upload
                        }
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-700 font-medium hover:underline"
                      >
                        {typeof formData.supporting_documents === "string"
                          ? formData.supporting_documents.split("/").pop()
                          : formData.supporting_documents.name || "Not named"}
                      </Typography>
                    </div>
                    <IconButton
                      onClick={(e) => handleRemoveFile(e)}
                      size="small"
                      className="!text-red-500"
                    >
                      <X size={16} />
                    </IconButton>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto text-gray-400 mb-2" size={24} />
                    <p className="text-gray-500">Upload Document</p>
                    <p className="text-xs text-gray-500 mt-1">
                      PDF, JPG, PNG, DOCX up to 10MB
                    </p>
                  </>
                )}
              </div>
              {errors[`supporting_documents`] && (
                <Typography variant="caption" color="error">
                  {errors[`supporting_documents`]}
                </Typography>
              )}
            </Grid>
          </Grid>
          {/* <Box>
          <InputLabel>Supporting Documents</InputLabel>
          <input
            type="file"
            accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
            onChange={handleFileChange}
          />
          {formData.supporting_documents && (
            <Box mt={1} className="flex items-center gap-2">
              <FileText size={18} className="text-blue-500" />
              <Typography
                component="a"
                href={
                  typeof formData.supporting_documents === "string"
                    ? formData.supporting_documents
                    : URL.createObjectURL(formData.supporting_documents)
                }
                target="_blank"
                rel="noopener noreferrer"
              >
                {typeof formData.supporting_documents === "string"
                  ? formData.supporting_documents.split("/").pop()
                  : formData.supporting_documents.name}
              </Typography>
            </Box>
          )}
        </Box> */}

          {/* Buttons */}
          <div className="flex justify-end space-x-2 mt-4">
            <Button onClick={onCancel}>Cancel</Button>
            <Button type="submit" variant="contained">
              Save
            </Button>
          </div>
        </LocalizationProvider>
      </form>
      <Snackbar
        open={!!fileError}
        autoHideDuration={6000}
        onClose={handleCloseError}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseError}
          severity="error"
          className="flex items-center"
        >
          {fileError}
        </Alert>
      </Snackbar>
    </>
  );
};

export default EmploymentHistoryForm;
